import { useEffect } from "react";
import Avatar from "src/components/Avatar";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Loader from "src/components/Loader";
import Modal from "src/components/Modal";
import ResumeCard from "src/components/ResumeCompanyUser/resume-card/ResumeCard";
import RegistrationDetails from "src/components/tasks/panes/registration/RegistrationDetails";
import useResume from "src/hooks/api/services/resume/useResume";
import useGet from "src/hooks/rest/useGet";

interface RegistrationDetailModalProps extends Translation {
	registrationId: number | string;
	modal: {
		visible: boolean;
		onClose: () => void;
	};
}

const RegistrationDetailModal = ({
	t,
	registrationId,
	modal,
}: RegistrationDetailModalProps) => {
	const [{ data: registration }] = useGet(
		`/company/registrations/${registrationId}`
	);
	const color = registration?.user?.primary_color;
	const { resume, actions, status } = useResume({
		rememberKey: registration?.user?.hid,
	});
	const modules = resume?.resume_modules || [];

	useEffect(() => {
		actions.get(registration?.user?.hid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [registration]);

	return (
		<Modal
			visible={modal.visible}
			onClose={modal.onClose}
			head="floating"
			size="sidebar"
		>
			{status === "idle" &&
			resume.id &&
			registration?.user?.hid === resume?.hid ? (
				<ResumeCard
					{...{ resume }}
					options={{
						title: {
							variant: "uppercase",
						},
					}}
				>
					<div className="-mx-8 -mt-8 mb-8">
						<div
							style={{
								backgroundColor: color,
							}}
							className="p-8 py-16"
						></div>
						<div className="px-8 -mt-12">
							<div className="flex gap-4 items-end">
								<Avatar size="large" src={resume.avatar} />
								<h3 className="text-left text-xxl">
									{resume.full_name}
								</h3>
							</div>
						</div>
					</div>
					<RegistrationDetails
						variant="vertical"
						{...{ registration }}
					/>

					<div className="my-4"></div>
					{modules.includes("experiences.education") && (
						<Section title={t("sections.education")}>
							<ResumeCard.Education />
						</Section>
					)}

					{modules.includes("experiences.job") && (
						<Section title={t("sections.job")}>
							<ResumeCard.Work />
						</Section>
					)}
					{modules.includes("skills") && (
						<Section title={t("sections.skills")}>
							<ResumeCard.Skills />
						</Section>
					)}

					{modules.includes("social-media") && (
						<Section title={t("sections.social-media")}>
							<ResumeCard.SocialMedia />
						</Section>
					)}
					{resume.badges.length > 0 && (
						<Section title={t("sections.badges")}>
							<ResumeCard.Badge />
						</Section>
					)}

					{modules.includes("reviews") && (
						<Section title={t("sections.reviews")}>
							<ResumeCard.Reviews />
						</Section>
					)}
				</ResumeCard>
			) : (
				<div className="flex flex-col flex-1 items-center justify-center">
					<Loader />
				</div>
			)}
		</Modal>
	);
};

RegistrationDetailModal.locale = {
	nl: {
		title: "Aanmelding",
		sections: {
			education: "Opleidingen & cursussen",
			job: "Werkervaring",
			skills: "Vaardigheden",
			"social-media": "Social media",
			badges: "Badges",
			reviews: "Reviews",
		},
	},
};

interface SectionProps {
	title: string;
	children?: any;
}

const Section = ({ title, children }: SectionProps) => {
	return (
		<div className="flex flex-col gap-4">
			<h3>{title}</h3>
			<div className="">{children}</div>
		</div>
	);
};

export default withTranslation(RegistrationDetailModal);
